/**
 * <hugh-sock-finder>: the sock finder dialog for sections/hugh-sock-finder.liquid.
 * A "who" tile opens the dialog: what matters → height → best matches (the three most reviewed socks
 * that fit) and a link to all of them, filtered, in the results collection. Counts and matches come
 * from the products embedded in the page; options with no match are disabled (hidden by CSS).
 * Results are built with DOM methods and textContent only. Without JS the tiles are plain links.
 */
const G = 'filter.p.m.custom.gender';
const F = 'filter.p.m.custom.features';
const L = 'filter.p.m.custom.length';

/** @param {unknown} value @returns {string[]} */
const list = (value) => (Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)]);

/** Same-origin paths only. @param {unknown} href */
const safePath = (href) => (typeof href === 'string' && href.startsWith('/') && !href.startsWith('//') ? href : '');

class HughSockFinder extends HTMLElement {
  /** @type {Array<{ g: string[], f: string[], l: string[], t: string, u: string, i: string, p: string, r: number, n: number }>} */
  #products = [];
  #who = '';
  #step = 1;
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    try {
      const raw = JSON.parse(this.querySelector('[data-sf-products]')?.textContent || '[]');
      this.#products = (Array.isArray(raw) ? raw : []).map((p) => ({
        g: list(p.g), f: list(p.f), l: list(p.l),
        t: String(p.t || ''), u: safePath(p.u), i: typeof p.i === 'string' ? p.i : '', p: String(p.p || ''),
        r: Number(p.r) || 0, n: Number(p.n) || 0,
      }));
    } catch {
      this.#products = [];
    }
    this.querySelectorAll('[data-who]').forEach((tile) =>
      tile.addEventListener('click', (event) => {
        event.preventDefault();
        this.#open(tile.getAttribute('data-who') || '');
      }, { signal })
    );
    const dialog = this.#dialog;
    dialog?.addEventListener('change', this.#onChange, { signal });
    dialog?.querySelector('[data-close]')?.addEventListener('click', () => dialog.close(), { signal });
    dialog?.querySelector('[data-back]')?.addEventListener('click', () => this.#go(this.#step - 1), { signal });
    // A click on the backdrop closes it.
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  /** @param {string} key */
  #t(key) {
    try {
      return JSON.parse(this.querySelector('[data-sf-i18n]')?.textContent || '{}')[key] || '';
    } catch {
      return '';
    }
  }

  /** @param {string} who */
  #open(who) {
    const dialog = this.#dialog;
    if (!dialog) return;
    this.#who = who;
    dialog.querySelectorAll('input:checked').forEach((i) => { if (i instanceof HTMLInputElement) i.checked = false; });
    dialog.showModal();
    this.#go(1);
  }

  /** @param {string} name */
  #value(name) {
    const input = this.#dialog?.querySelector(`input[name="${name}"]:checked`);
    return input instanceof HTMLInputElement ? input.value : '';
  }

  #genders() {
    return this.#who === 'Women' || this.#who === 'Men' ? [this.#who, 'Unisex'] : this.#who ? [this.#who] : [];
  }

  /** @param {string} need @param {string} height */
  #matches(need, height) {
    const genders = this.#genders();
    return this.#products.filter(
      (p) => (!genders.length || p.g.some((g) => genders.includes(g))) && (!need || p.f.includes(need)) && (!height || p.l.includes(height))
    );
  }

  /** @param {number} step */
  #go(step) {
    const dialog = this.#dialog;
    if (!dialog) return;
    if (step < 1) return dialog.close();
    this.#step = Math.min(3, step);
    // The "who" tile on the page was step 1, so the dialog shows steps 2 and 3, then the matches.
    const shown = Math.min(3, this.#step + 1);
    dialog.dataset.at = String(shown);
    if (this.#step === 1) this.#counts('need', (v) => this.#matches(v, '').length);
    if (this.#step === 2) this.#counts('height', (v) => this.#matches(this.#value('need'), v).length);
    if (this.#step === 3) this.#results();
    let current = null;
    dialog.querySelectorAll('[data-step]').forEach((el) => {
      const on = el.getAttribute('data-step') === String(this.#step);
      el.classList.toggle('is-current', on);
      if (on) current = el;
    });
    const q = dialog.querySelector('[data-question]');
    if (q && current) q.textContent = /** @type {HTMLElement} */ (current).dataset.q || '';
    const text = dialog.querySelector('[data-progress-text]');
    if (text) text.textContent = this.#t('step').replace('[n]', String(shown));
    dialog.querySelector('.hugh-sf__body')?.scrollTo({ top: 0 });
  }

  /** @param {string} name @param {(value: string) => number} count */
  #counts(name, count) {
    this.#dialog?.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const n = count(input.value);
      input.disabled = n === 0;
      const el = input.closest('label')?.querySelector('[data-count]');
      if (el) el.textContent = String(n);
    });
  }

  #results() {
    const dialog = this.#dialog;
    const box = dialog?.querySelector('[data-results]');
    if (!dialog || !box) return;
    const need = this.#value('need');
    const height = this.#value('height');
    const found = this.#matches(need, height);
    const best = [...found].sort((a, b) => b.n - a.n || b.r - a.r).slice(0, 3);
    box.replaceChildren(...best.map((p) => this.#card(p)));

    const all = dialog.querySelector('[data-all]');
    if (all instanceof HTMLAnchorElement) {
      const url = new URL(this.dataset.shelf || '/collections/all', window.location.origin);
      for (const g of this.#genders()) url.searchParams.append(G, g);
      if (need) url.searchParams.append(F, need);
      if (height) url.searchParams.append(L, height);
      all.href = url.origin === window.location.origin ? url.pathname + url.search : '/collections/all';
      all.textContent = this.#t('all').replace('[n]', String(found.length));
    }
  }

  /** @param {{ t: string, u: string, i: string, p: string, r: number, n: number }} p */
  #card(p) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.className = 'hugh-sf__match';
    a.href = p.u || '/collections/all';
    if (p.i) {
      const img = document.createElement('img');
      img.src = p.i.startsWith('//') ? `https:${p.i}` : p.i;
      img.alt = '';
      img.width = 88;
      img.height = 88;
      img.loading = 'lazy';
      a.append(img);
    }
    const text = document.createElement('span');
    text.className = 'hugh-sf__match-text';
    const title = document.createElement('span');
    title.className = 'hugh-sf__match-title';
    title.textContent = p.t;
    text.append(title);
    if (p.n > 0) {
      const meta = document.createElement('span');
      meta.className = 'hugh-sf__match-meta';
      meta.textContent = this.#t('reviews').replace('[r]', p.r.toFixed(1)).replace('[n]', p.n.toLocaleString('en-US'));
      text.append(meta);
    }
    const price = document.createElement('span');
    price.className = 'hugh-sf__match-price';
    price.textContent = p.p;
    text.append(price);
    a.append(text);
    li.append(a);
    return li;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    window.setTimeout(() => this.#go(this.#step + 1), 160);
  };
}

if (!customElements.get('hugh-sock-finder')) customElements.define('hugh-sock-finder', HughSockFinder);
